import {
	HistoryInteractionHandlingFilters,
	HistoryInteractionMiscellaneousFilters,
	InteractionHistoryFilter,
	InteractionHistoryListItemDto,
	Note,
} from "@/components/organisms/history/types";
import { Client, mapUrl } from "../client";
import { Controller } from "../controller";
import { Endpoint } from "../endpoint";
import { ExtendedPagedResult } from "../pagedResult";
import { AxiosResponse } from "axios";
import { InteractionType } from "@/common/types";
import { InfiniteQueryFnParams } from "@/hooks/infiniteQuery";
import { GetHistoryRequest, HistoryInteractionTypes } from "../models/history";

export const historyQueryKey = "history";
export const lastHandledAgentNameQueryKey = "lastHandledAgentName";

export const fetchHistoryItems = ({
	page,
	searchValue: search,
	sorting,
	filter,
}: InfiniteQueryFnParams): Promise<
	ExtendedPagedResult<InteractionHistoryListItemDto>
> => {
	const interactionHistoryFilter = filter as InteractionHistoryFilter;

	const pagingFilter: GetHistoryRequest = {
		pageNumber: page,
		pageSize: 12,
		filter: search,
		sortBy:
			(sorting &&
				sorting.column &&
				sorting.direction &&
				`${sorting.column} ${sorting.direction}`) ||
			undefined,
		queueIds: interactionHistoryFilter?.queueIds,
		supervisedOrOperatedQueueIds:
			interactionHistoryFilter?.supervisedOrOperatedQueueIds,
		types: getInteractionHistoryFilterMediaTypes(
			interactionHistoryFilter?.types
		),
		isHandled: getInteractionHistoryFilterHandling(
			interactionHistoryFilter?.handling
		),
		isCallback:
			interactionHistoryFilter?.types?.includes(
				HistoryInteractionTypes.Callback
			) || undefined,
		isMissed:
			interactionHistoryFilter?.miscellaneous?.includes(
				HistoryInteractionMiscellaneousFilters.Missed
			) || undefined,
		isTransferred:
			interactionHistoryFilter?.miscellaneous?.includes(
				HistoryInteractionMiscellaneousFilters.Transferred
			) || undefined,
		isOutbound:
			interactionHistoryFilter?.miscellaneous?.includes(
				HistoryInteractionMiscellaneousFilters.Outbound
			) || undefined,
		isInbound:
			interactionHistoryFilter?.miscellaneous?.includes(
				HistoryInteractionMiscellaneousFilters.Inbound
			) || undefined,
		showMyInteractions:
			interactionHistoryFilter?.showMyInteractions || undefined,
	};

	return Client.instance.post(
		mapUrl(Controller.History, Endpoint.Interactions),
		pagingFilter
	);
};

const getInteractionHistoryFilterHandling = (
	interactionHistoryHandlingFilters?: HistoryInteractionHandlingFilters[]
): boolean | undefined =>
	interactionHistoryHandlingFilters?.includes(
		HistoryInteractionHandlingFilters.Handled
	) &&
	interactionHistoryHandlingFilters.includes(
		HistoryInteractionHandlingFilters.NotHandled
	)
		? undefined
		: interactionHistoryHandlingFilters?.includes(
					HistoryInteractionHandlingFilters.Handled
			  )
			? true
			: interactionHistoryHandlingFilters?.includes(
						HistoryInteractionHandlingFilters.NotHandled
				  )
				? false
				: undefined;

const getInteractionHistoryFilterMediaTypes = (
	interactionHistoryTypes?: HistoryInteractionTypes[]
): InteractionType[] => {
	const types = new Set<InteractionType>();

	interactionHistoryTypes?.forEach((iht) => {
		switch (iht) {
			case HistoryInteractionTypes.Call:
				types.add(InteractionType.Call);
				break;
			case HistoryInteractionTypes.Email:
				types.add(InteractionType.Email);
				break;
			case HistoryInteractionTypes.Generic:
				types.add(InteractionType.Generic);
				break;
			case HistoryInteractionTypes.WhatsApp:
				types.add(InteractionType.WhatsApp);
				break;
			case HistoryInteractionTypes.Webchat:
				types.add(InteractionType.Webchat);
				break;
			case HistoryInteractionTypes.Voicemail:
				types.add(InteractionType.Voicemail);
				break;
		}
	});

	return [...types];
};

export const createInteractionNote = (note: Note): Promise<AxiosResponse> => {
	return Client.instance.post(mapUrl(Controller.History, Endpoint.Notes), note);
};

export const getNotesByInteractionId = (
	interactionId: string
): Promise<Note[]> => {
	return Client.instance.get(mapUrl(Controller.History, Endpoint.Notes), {
		interactionId: interactionId,
	});
};

export const getLastAgentNameForInteraction = (
	type: InteractionType,
	mediaId: string
): Promise<string> => {
	const url = mapUrl(Controller.History, type.toString(), mediaId);

	return Client.instance.get<string>(url);
};

export interface RecordingChunkUrl {
	recordingId: string;
	url: string;
}

export const getCallRecording = (sessionId: string): Promise<RecordingChunkUrl[]> => {
	return Client.instance.get(
		mapUrl(Controller.History, sessionId, Endpoint.Recording)
	);
};

export const auditHistoryViewDetails = (sessionId: string): Promise<void> => {
	return Client.instance.post(
		mapUrl(Controller.History, sessionId, "audit-view-details"),
		{}
	);
};

export const auditRecordingPlay = (recordingId: string): Promise<void> => {
	return Client.instance.post(
		mapUrl(Controller.History, "recordings", recordingId, "audit-play"),
		{}
	);
};

export const auditRecordingDownload = (recordingId: string): Promise<void> => {
	return Client.instance.post(
		mapUrl(Controller.History, "recordings", recordingId, "audit-download"),
		{}
	);
};
